import * as R from 'ramda'
import { createSelector } from 'reselect'
import moment from 'moment'

const formatDate = createSelector(
  date => date,
  date => moment(date).format('MM/DD/YYYY')
)

// All selectors are based on a Board object
const boardSelector = board => board

const boardSettingsSelector = createSelector(
  boardSelector,
  R.propOr({}, ':board/settings')
)

// board => Boolean (whether points are turned on)
export const pointsEnabledSelector = createSelector(
  boardSelector,
  R.path([':board/settings', ':board-settings/grading-enabled'])
)

// board => Int
export const pointMaxSelector = createSelector(
  boardSelector,
  R.path([':board/settings', ':board-settings/points-target-max'])
)

// board => Object
const pointConfigSelector = createSelector(
  boardSelector,
  R.prop(':board/point-configurations')
)

// board => Object
const currentPointConfigurationSelector = createSelector(
  pointConfigSelector,
  pointConfigs =>
    !R.isEmpty(pointConfigs) &&
    !R.isNil(pointConfigs) &&
    R.last(R.sortBy(R.prop(':system/created-at'), pointConfigs))
)

const pointModifiersSelector = createSelector(
  currentPointConfigurationSelector,
  R.propOr([], ':point-configuration/point-modifiers')
)

export const topicPointValueMapSelector = createSelector(
  pointModifiersSelector,
  R.pipe(
    R.filter(R.propEq(':point-modifier/type', ':limiting-topic')),
    R.reduce(
      (
        acc,
        { ':point-modifier/entity': entity, ':point-modifier/value': value }
      ) => {
        if (entity) {
          return {
            ...acc,
            [entity[':db/id'] || entity]: value
          }
        }
        return acc
      },
      {}
    )
  )
)

// board => Object
export const gradingPeriodsSelector = createSelector(
  boardSelector,
  R.propOr([], ':board/grading-periods')
)

export const gradingPeriodsLengthSelector = createSelector(
  gradingPeriodsSelector,
  R.length
)

// board => Object
const firstGradingPeriodSelector = createSelector(
  gradingPeriodsSelector,
  R.head
)
// board => Object
const lastGradingPeriodSelector = createSelector(
  gradingPeriodsSelector,
  R.when(R.complement(R.isNil), R.last)
)

// board => Date (unix)
export const startDateSelector = createSelector(
  firstGradingPeriodSelector,
  R.prop(':grading-period/start-date')
)

export const startDateDisabledSelector = createSelector(
  startDateSelector,
  R.gt(1),
  R.not
)

// board => Date (unix)
export const endDateSelector = createSelector(
  lastGradingPeriodSelector,
  R.prop(':grading-period/end-date')
)

export const endDateDisabledSelector = createSelector(
  endDateSelector,
  R.lt(30000000000000)
)

// board => Date (moment)
export const startDateFormattedSelector = createSelector(
  startDateSelector,
  formatDate
)

export const currentGradingPeriodIndexSelector = createSelector(
  gradingPeriodsSelector,
  R.findLastIndex(
    R.propSatisfies(R.lte(R.__, Date.now()), ':grading-period/start-date')
  )
)

export const currentGradingPeriodSelector = createSelector(
  currentGradingPeriodIndexSelector,
  gradingPeriodsSelector,
  R.prop
)

export const currentGradingPeriodEndDateSelector = createSelector(
  currentGradingPeriodSelector,
  R.prop(':grading-period/end-date')
)

const nextGradingPeriodIndexSelector = createSelector(
  currentGradingPeriodIndexSelector,
  R.inc
)

const nextGradingPeriodSelector = createSelector(
  nextGradingPeriodIndexSelector,
  gradingPeriodsSelector,
  R.prop
)

export const nextGradingPeriodStartDateSelector = createSelector(
  nextGradingPeriodSelector,
  R.prop(':grading-period/start-date')
)

// board => Boolean
export const pointsConfiguredSelector = createSelector(
  pointsEnabledSelector,
  pointConfigSelector,
  gradingPeriodsLengthSelector,
  (enabled, pointConfigs, numPeriods) => {
    return !!(enabled && pointConfigs && numPeriods)
  }
)

// board => Boolean
export const withinGradingRangeSelector = createSelector(
  pointsConfiguredSelector,
  gradingPeriodsSelector,
  (isConfigured, periods) =>
    isConfigured &&
    !!periods &&
    !!periods.length &&
    Date.now() >= R.prop(':grading-period/start-date', R.head(periods)) &&
    Date.now() <= R.prop(':grading-period/end-date', R.last(periods))
)

// board => Object
export const metricsSelector = createSelector(boardSelector, board =>
  R.propOr(R.prop('@board/metrics', board), 'userBoardMetrics', board)
)

// board => Int
export const participationPointsSelector = createSelector(
  metricsSelector,
  R.prop('cappedTotal')
)

// board => Int
export const participationGradeSelector = createSelector(
  metricsSelector,
  R.prop('participationGrade')
)

// board => Int
const uncappedParticipationGradeSelector = createSelector(
  metricsSelector,
  R.propOr(0, 'uncappedParticipationGrade')
)

// Int => Float (percent)
export const gradeToPercentSelector = createSelector(
  R.identity,
  grade =>
    `${Math.min(100, (parseFloat(grade) * 100).toFixed(1))}%${
      grade > 1 ? ' (+)' : ''
    }`
)

// board => Int
export const participationGradePercentSelector = createSelector(
  uncappedParticipationGradeSelector,
  gradeToPercentSelector
)

const elapsedWeeksSelector = createSelector(
  metricsSelector,
  R.prop('elapsedWeeks')
)

// board => Int
export const weeklyScoreSelector = createSelector(
  metricsSelector,
  R.path(['weeklyScore', 0])
)

// board => Int
export const weeklyMaxSelector = createSelector(
  metricsSelector,
  R.path(['weeklyScore', 1])
)

export const weeklyTargetSelector = createSelector(
  pointMaxSelector,
  gradingPeriodsLengthSelector,
  R.divide
)

const periodMaxSelector = createSelector(
  participationPointsSelector,
  weeklyScoreSelector,
  weeklyMaxSelector,
  (current, weekCurrent, weekMax) => {
    return current - weekCurrent + weekMax
  }
)

export const periodTargetSelector = createSelector(
  weeklyTargetSelector,
  elapsedWeeksSelector,
  R.multiply
)

export const freeformPointsActiveSelector = createSelector(
  boardSettingsSelector,
  boardSettings =>
    R.prop(':board-settings/grading-enabled', boardSettings) &&
    R.prop(':board-settings/alternative-points-system', boardSettings) ===
      ':freeform-points'
)

export const distanceToGoalSelector = createSelector(
  metricsSelector,
  pointMaxSelector,
  (metrics, pointMax) => pointMax - R.propOr(0, 'cappedTotal', metrics)
)

const remainingPeriodsCountSelector = createSelector(
  gradingPeriodsLengthSelector,
  elapsedWeeksSelector,
  R.subtract
)

const pointLogSelector = createSelector(boardSelector, R.propOr([], 'log'))

const currentPointLogSelector = createSelector(
  pointLogSelector,
  R.findLast(
    R.pathSatisfies(R.lte(R.__, Date.now()), [0, ':grading-period/start-date'])
  )
)

export const currentPeriodPointAdjustmentSelector = createSelector(
  currentPointLogSelector,
  R.pathOr(0, [0, 'pointAdjustment'])
)

export const periodEarnablePointsSelector = createSelector(
  periodMaxSelector,
  participationPointsSelector,
  currentPeriodPointAdjustmentSelector,
  (periodMax, current, pointAdjustment) => {
    return periodMax + pointAdjustment - current
  }
)

const remainingEarnablePointsSelector = createSelector(
  remainingPeriodsCountSelector,
  weeklyMaxSelector,
  R.multiply
)

const totalEarnablePointsSelector = createSelector(
  remainingEarnablePointsSelector,
  periodEarnablePointsSelector,
  R.add
)

const totalPointAdjustmentSelector = createSelector(
  metricsSelector,
  R.prop('pointAdjustment')
)

export const pointStatusSelector = createSelector(
  pointsConfiguredSelector,
  startDateSelector,
  endDateSelector,
  pointMaxSelector,
  participationPointsSelector,
  periodMaxSelector,
  periodTargetSelector,
  totalEarnablePointsSelector,
  distanceToGoalSelector,
  currentGradingPeriodEndDateSelector,
  currentPeriodPointAdjustmentSelector,
  totalPointAdjustmentSelector,
  (
    pointsConfigured,
    startDate,
    endDate,
    total,
    earned,
    periodMax,
    periodTarget,
    totalEarnable,
    distance,
    periodEndDate,
    currentPeriodPointAdjustment,
    totalPointAdjustment
  ) => {
    if (!pointsConfigured) {
      return 'not-configured'
    }

    if (Date.now() < startDate) {
      return 'not-started'
    }

    if (Date.now() > endDate) {
      return 'ended'
    }

    if (total === -1) {
      return 'no-goal'
    }

    if (earned >= total + totalPointAdjustment) {
      return 'complete'
    }

    if (earned >= periodMax + currentPeriodPointAdjustment) {
      return 'max-reached'
    }

    if (earned > periodTarget) {
      return 'ahead'
    }

    if (totalEarnable < distance) {
      return 'total-unreachable'
    }

    if (periodMax < periodTarget) {
      return 'target-unreachable'
    }

    if (earned >= periodTarget) {
      return 'target-reached'
    }

    const day = 86400000
    const timeLeft = periodEndDate - Date.now()

    if (timeLeft < day) {
      return 'hours-left'
    }
    if (timeLeft < 2 * day) {
      return 'days-left'
    }
  }
)
